import { useEffect, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import { Modal } from "../shared-components/Modal";
import { usePermissionValidator } from "../api/UserPermissionValidators";
import { useCreateReportNote, useGetReportsNotes } from "../api/ReportsApi";
import { useApplicationToast } from "../webcontent/ApplicationToastProvider";
import AddNoteModal from "../reports/AddNoteModal";
import { HARDCODED_ACTOR_UUID } from "../api/helpers";
import { ReportSummaryItemApi, ReportLog } from "../api/Reports";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { classNames, formattedDate, LONG_TEXT_WRAP_CLASSES } from "../utils";

type Notes360Props = {
  isReadOnly: boolean;
  report: ReportSummaryItemApi;
};

function Notes360({ isReadOnly, report }: Notes360Props) {
  const { canAddNotes } = usePermissionValidator();
  const [isReportNoteModalOpen, setIsReportNoteModalOpen] = useState(false);
  const {
    isLoading: notesLoading,
    data: notes,
    mutate: getNotes,
  } = useGetReportsNotes();
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [newNote, setNewNote] = useState("");
  const { isLoading: createNotePending, mutateAsync: createReportNote } =
    useCreateReportNote();
  const { showPushToast } = useApplicationToast();

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      showPushToast?.({
        message: "Please enter a note before adding",
        type: "warning",
      });
      return;
    }
    try {
      await createReportNote({
        report_uuid: report.report_uuid,
        note: newNote,
        actor_uuid: HARDCODED_ACTOR_UUID,
      });
      setIsReportNoteModalOpen(false);
      setNewNote("");
    } catch (err: unknown) {
      let message = "Failed to add note";
      if (err && typeof err === "object" && "response" in err) {
        const detail = (err as { response?: { data?: { detail?: unknown } } })
          .response?.data?.detail;
        if (typeof detail === "string") message = detail;
        else if (Array.isArray(detail))
          message = detail.map((d: { msg?: string }) => d.msg ?? "").join(", ");
      } else if (err instanceof Error) {
        message = err.message;
      }
      showPushToast?.({ message, type: "error" });
    }
  };

  const handleCancelNote = () => {
    setIsReportNoteModalOpen(false);
    setNewNote("");
  };

  useEffect(() => {
    if (createNotePending) return;
    getNotes(report.report_uuid);
  }, [getNotes, report.report_uuid, createNotePending]);

  useEffect(() => {
    if (notes?.length) setShowAllNotes(false);
  }, [notes]);

  if (!canAddNotes) {
    return null;
  }

  const displayNotes = notes ?? [];
  const visibleNotes = showAllNotes ? displayNotes : displayNotes.slice(0, 1);

  return (
    <>
      <div className="px-4 pt-4 pb-4 bg-gray-100 rounded-b-lg min-w-0">
        <div className="flex justify-between items-start gap-2 mb-3 min-w-0">
          <span
            className={classNames(
              "font-bold text-lg text-gray-900 flex-1 pr-2",
              LONG_TEXT_WRAP_CLASSES
            )}
          >
            Notes
          </span>
          {!isReadOnly && (
            <Button
              text="Add"
              type={ButtonTypes.add}
              colorType={ButtonColorTypes.cancel}
              onClick={() => setIsReportNoteModalOpen(true)}
              className="!py-1.5 !px-3 text-sm shrink-0"
            />
          )}
        </div>
        {notesLoading ? (
          <div className="p-2">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {visibleNotes.map((note: ReportLog) => (
              <div
                key={note.external_id}
                className="rounded-lg border border-gray-200 bg-white p-3 text-sm shadow-sm min-w-0"
              >
                <div
                  className={classNames(
                    "font-semibold text-gray-900",
                    LONG_TEXT_WRAP_CLASSES
                  )}
                >
                  {note.name}
                </div>
                <div
                  className={classNames(
                    "text-gray-500 text-xs mb-1.5",
                    LONG_TEXT_WRAP_CLASSES
                  )}
                >
                  {formattedDate(note.created_at)}
                </div>
                <p
                  className={classNames(
                    "text-gray-600 whitespace-pre-wrap text-sm leading-relaxed",
                    LONG_TEXT_WRAP_CLASSES
                  )}
                >
                  {note.log_message}
                </p>
              </div>
            ))}
            {displayNotes.length > 1 && !showAllNotes && (
              <button
                type="button"
                className="flex items-center justify-center gap-0.5 w-full text-ourGreen font-medium hover:underline text-xs"
                onClick={() => setShowAllNotes(true)}
              >
                See more
                <ChevronDownIcon className="w-3 h-3 flex-shrink-0" />
              </button>
            )}
            {displayNotes.length > 1 && showAllNotes && (
              <button
                type="button"
                className="flex items-center justify-center gap-0.5 w-full text-ourGreen font-medium hover:underline text-xs"
                onClick={() => setShowAllNotes(false)}
              >
                See less
                <ChevronDownIcon className="w-3 h-3 flex-shrink-0 rotate-180" />
              </button>
            )}
          </div>
        )}
      </div>
      <Modal
        open={isReportNoteModalOpen}
        onClose={() => setIsReportNoteModalOpen(false)}
        width="w-full max-w-lg p-0"
      >
        <AddNoteModal
          note={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          onCancel={handleCancelNote}
          onSubmit={handleAddNote}
        />
      </Modal>
      {createNotePending && <LoadingSpinner />}
    </>
  );
}

export default Notes360;
